import { useCallback, useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { api } from "@/lib/api"
import { CalendarPlus, CalendarX, Loader2, Pencil, Plus, Trash2, X } from "lucide-react"
import * as outreachApi from "../api/outreach.api"
import { BookCallDialog, type BookCallChoice } from "./BookCallDialog"
import { ChannelPauseControl } from "./ChannelPauseControl"
import { OUT_OF_HOURS_PROMPT, describeConfirmation, describePreference, needsConfirmation } from "../lib/preference"
import { DAY_END, DAY_START, WEEKDAY_LABELS, formatInTz, toMinutes } from "../lib/time"
import type { ContactPreference, ContactWindow, NeedsConfirmation, PausedChannels } from "../types/outreach.types"

interface Props {
  patientId: string
  patientName: string
  onChanged?: () => void
}

interface Enrollment {
  id: string
  definition_id: string
  name: string
  status: string
  paused_channels?: PausedChannels
  pause_reason?: string | null
}

interface PendingCall {
  at: string
  requestedBy?: string
}

/** A preference counts as the patient's own choice unless staff typed it without the patient's words. */
const isPatientChosen = (p: ContactPreference | null) =>
  !!p?.windows?.length && (p.source !== "STAFF" || !!p.raw_text?.trim())

const emptyWindow = (): ContactWindow => ({ weekdays: [0, 1, 2, 3, 4], start: "10:00", end: "10:00" })

/**
 * Outreach controls in the APCM patient drawer:
 *  - per-enrollment channel pauses (POST /outreach/pause scope "enrollment")
 *  - contact preference (GET/PUT /outreach/patients/{pid}/contact-preference)
 *  - book / move / cancel the pending call (POST/DELETE /outreach/patients/{pid}/call)
 */
export function PatientOutreachPanel({ patientId, patientName, onChanged }: Props) {
  const { toast } = useToast()
  const [enrollments, setEnrollments] = useState<Enrollment[] | null>(null)
  const [pref, setPref] = useState<ContactPreference | null>(null)
  const [prefLoaded, setPrefLoaded] = useState(false)
  const [pending, setPending] = useState<PendingCall | null>(null)
  const [bookOpen, setBookOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [editing, setEditing] = useState(false)

  const load = useCallback(async () => {
    const [enr, p, call] = await Promise.all([
      api.getPatientCampaigns(patientId).catch(() => []),
      outreachApi.getContactPreference(patientId).catch(() => null),
      outreachApi.getPendingCall(patientId).catch(() => null),
    ])
    setEnrollments(
      (enr || [])
        .filter((e) => ["ACTIVE", "PAUSED"].includes((e.status || "").toUpperCase()))
        .map((e) => {
          const raw = e as unknown as Enrollment
          return {
            id: e.id,
            definition_id: e.definition_id,
            name: e.name,
            status: e.status,
            paused_channels:
              raw.paused_channels !== undefined
                ? raw.paused_channels
                : (e.status || "").toUpperCase() === "PAUSED"
                  ? "ALL"
                  : null,
            pause_reason: e.pause_reason,
          }
        }),
    )
    setPref(p)
    setPrefLoaded(true)
    setPending(call?.schedule_timestamp ? { at: call.schedule_timestamp, requestedBy: call.requested_by } : null)
  }, [patientId])

  useEffect(() => {
    setEnrollments(null)
    setPrefLoaded(false)
    setEditing(false)
    load()
  }, [load])

  const book = async (choice: BookCallChoice) => {
    try {
      const res = await outreachApi.bookCall(patientId, {
        ...(choice.at ? { at: choice.at } : {}),
        ...(choice.usePreference ? { use_preference: true } : {}),
      })
      const ok = res.booked ?? res.moved ?? false
      if (!ok) {
        toast({ title: "Call not booked", description: res.reason || "Refused by the contact policy", variant: "destructive" })
        throw new Error(res.reason || "not booked")
      }
      const when = res.scheduled_call?.schedule_timestamp
      toast({
        title: pending ? "Call moved" : "Call booked",
        description: [
          when ? formatInTz(when) : null,
          res.reminder_sms_ids?.length ? `${res.reminder_sms_ids.length} reminder text scheduled` : "No reminder text",
        ]
          .filter(Boolean)
          .join(" · "),
      })
      if (when) setPending({ at: when, requestedBy: res.scheduled_call?.requested_by })
      onChanged?.()
    } catch (err: any) {
      if (err?.response) {
        toast({ title: "Call not booked", description: outreachApi.errorMessage(err), variant: "destructive" })
      }
      throw err
    }
  }

  const cancel = async () => {
    setCancelling(true)
    try {
      const res = await outreachApi.cancelCall(patientId)
      toast({
        title: res.cancelled ? "Call cancelled" : "No pending call to cancel",
        description: res.cancelled_sms_ids?.length ? `${res.cancelled_sms_ids.length} reminder text cancelled too` : undefined,
      })
      setPending(null)
      setCancelOpen(false)
      onChanged?.()
    } catch (err) {
      toast({ title: "Cancel failed", description: outreachApi.errorMessage(err), variant: "destructive" })
    } finally {
      setCancelling(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Outreach</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {/* Pending call */}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Next call</p>
          <p className="font-medium">{pending ? `${formatInTz(pending.at)}${pending.requestedBy === "PATIENT" ? " (patient's time)" : ""}` : "No call booked"}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setBookOpen(true)}>
              <CalendarPlus className="h-3.5 w-3.5" /> {pending ? "Move call to…" : "Book call at…"}
            </Button>
            {pending && (
              <Button size="sm" variant="outline" className="gap-1.5 text-destructive hover:text-destructive" onClick={() => setCancelOpen(true)}>
                <CalendarX className="h-3.5 w-3.5" /> Cancel call
              </Button>
            )}
          </div>
        </div>

        {/* Per-enrollment pauses */}
        <div className="space-y-2 border-t pt-3">
          <p className="text-xs text-muted-foreground">Pause this patient</p>
          {enrollments === null ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : enrollments.length === 0 ? (
            <p className="text-muted-foreground">Not in an active campaign.</p>
          ) : (
            enrollments.map((e) => (
              <div key={e.id} className="space-y-1.5">
                <p className="font-medium">{e.name}</p>
                <ChannelPauseControl
                  scope="enrollment"
                  targetId={e.id}
                  subject={patientName}
                  pausedChannels={e.paused_channels}
                  currentReason={e.pause_reason}
                  onChange={(s) =>
                    setEnrollments((prev) =>
                      (prev || []).map((x) =>
                        x.id === e.id ? { ...x, paused_channels: s.paused_channels, pause_reason: s.reason } : x,
                      ),
                    )
                  }
                />
              </div>
            ))
          )}
        </div>

        {/* Contact preference */}
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Contact preference{pref?.source ? ` · from ${pref.source.toLowerCase()}` : ""}
            </p>
            {prefLoaded && !editing && (
              <Button size="sm" variant="ghost" className="h-7 gap-1.5" onClick={() => setEditing(true)}>
                <Pencil className="h-3 w-3" /> Edit
              </Button>
            )}
          </div>
          {!prefLoaded ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : editing ? (
            <PreferenceEditor
              patientId={patientId}
              initial={pref}
              hasPendingCall={!!pending}
              onCancel={() => setEditing(false)}
              onSaved={(p, movedTo) => {
                setPref(p)
                setEditing(false)
                if (movedTo) setPending({ at: movedTo, requestedBy: "PATIENT" })
                onChanged?.()
              }}
            />
          ) : (
            <>
              <p>{describePreference(pref)}</p>
              {pref?.raw_text && <p className="italic text-muted-foreground break-words">"{pref.raw_text}"</p>}
              {describeConfirmation(pref?.out_of_hours_confirmation) && (
                <p className="text-xs text-muted-foreground break-words">
                  {describeConfirmation(pref?.out_of_hours_confirmation)}
                </p>
              )}
              {pref?.pending_confirmation && (
                <p className="text-xs text-muted-foreground break-words">
                  Waiting for the patient to confirm an out-of-hours time
                  {pref.pending_confirmation.slot ? ` (${formatInTz(pref.pending_confirmation.slot)})` : ""}. Nothing
                  is booked for it until they say yes.
                </p>
              )}
            </>
          )}
        </div>
      </CardContent>

      <BookCallDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        patientName={patientName}
        pendingCallAt={pending?.at}
        preferenceSummary={isPatientChosen(pref) ? describePreference(pref) : null}
        onConfirm={book}
      />

      <Dialog open={cancelOpen} onOpenChange={(o) => !o && !cancelling && setCancelOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel {patientName}'s call?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  The call booked for <strong>{pending ? formatInTz(pending.at) : "-"}</strong> is cancelled. Any
                  reminder text for that call is cancelled too. Nothing is sent to the patient about it.
                </p>
                <p>
                  The campaign books its next attempt on its normal schedule. To stop calls completely, pause
                  Calls for this patient instead.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancelling}>
              Keep the call
            </Button>
            <Button variant="destructive" onClick={cancel} disabled={cancelling}>
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel call"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// ─── Preference editor ────────────────────────────────────────────

function PreferenceEditor({
  patientId,
  initial,
  hasPendingCall,
  onCancel,
  onSaved,
}: {
  patientId: string
  initial: ContactPreference | null
  hasPendingCall: boolean
  onCancel: () => void
  onSaved: (p: ContactPreference | null, movedTo: string | null) => void
}) {
  const { toast } = useToast()
  const [windows, setWindows] = useState<ContactWindow[]>(initial?.windows?.length ? initial.windows : [])
  const [notBefore, setNotBefore] = useState(initial?.not_before || "")
  const [avoid, setAvoid] = useState<string[]>(initial?.avoid_dates || [])
  const [avoidInput, setAvoidInput] = useState("")
  const [altPhone, setAltPhone] = useState(initial?.alternate_phone || "")
  const [language, setLanguage] = useState(initial?.language || "")
  const [rawText, setRawText] = useState(initial?.raw_text || "")
  const [movePending, setMovePending] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set when the server answered 409: the time is out of hours and the patient's yes is needed.
  const [confirmNeeded, setConfirmNeeded] = useState<NeedsConfirmation | null>(null)
  const [confirmText, setConfirmText] = useState("")
  // A confirmation covers only the time it was asked for: changing the time asks again.
  useEffect(() => setConfirmNeeded(null), [windows, notBefore, avoid])

  const updateWindow = (i: number, patch: Partial<ContactWindow>) =>
    setWindows((ws) => ws.map((w, j) => (j === i ? { ...w, ...patch } : w)))

  // An evening, early or weekend time is only allowed as the patient's own
  // choice, which staff record by typing the patient's words.
  const outsideDaytime = windows.some(
    (w) =>
      w.weekdays.some((d) => d > 4) ||
      toMinutes(w.start) < toMinutes(DAY_START) ||
      toMinutes(w.end) > toMinutes(DAY_END) ||
      toMinutes(w.start) >= toMinutes(DAY_END),
  )
  const needsWords = outsideDaytime && !rawText.trim()

  const save = async (confirmation?: string) => {
    if (needsWords) return
    setSaving(true)
    setError(null)
    try {
      const res = await outreachApi.putContactPreference(patientId, {
        windows,
        not_before: notBefore || null,
        avoid_dates: avoid,
        alternate_phone: altPhone.trim() || null,
        language: language || null,
        raw_text: rawText.trim() || null,
        move_pending_call: hasPendingCall && movePending,
        ...(confirmation ? { confirmed: true, confirmation_text: confirmation } : {}),
      })
      if (!res.saved) {
        setError(res.error || "Not saved")
        return
      }
      const movedTo = res.moved?.moved ? res.moved.scheduled_call?.schedule_timestamp || null : null
      toast({
        title: "Preference saved",
        description: movedTo
          ? `Call moved to ${formatInTz(movedTo)}`
          : res.moved && !res.moved.moved
            ? `Call not moved: ${res.moved.reason || "no fitting time"}`
            : undefined,
      })
      setConfirmNeeded(null)
      onSaved(res.preference, movedTo)
    } catch (err) {
      const needs = needsConfirmation(err)
      if (needs) {
        setConfirmNeeded(needs)
      } else {
        setError(outreachApi.errorMessage(err))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label>When to call (Central time)</Label>
        {windows.length === 0 && <p className="text-xs text-muted-foreground">No times set.</p>}
        {windows.map((w, i) => {
          const exact = w.start === w.end
          return (
            <div key={i} className="space-y-2 rounded-md border p-2">
              <div className="flex flex-wrap gap-1">
                {WEEKDAY_LABELS.map((d, idx) => {
                  const on = w.weekdays.includes(idx)
                  return (
                    <Button
                      key={d}
                      type="button"
                      size="sm"
                      variant={on ? "default" : "outline"}
                      className="h-7 px-2 text-xs"
                      onClick={() =>
                        updateWindow(i, { weekdays: on ? w.weekdays.filter((x) => x !== idx) : [...w.weekdays, idx].sort() })
                      }
                    >
                      {d}
                    </Button>
                  )
                })}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="time"
                  className="h-8 w-28"
                  value={w.start}
                  onChange={(e) => updateWindow(i, exact ? { start: e.target.value, end: e.target.value } : { start: e.target.value })}
                />
                {!exact && (
                  <>
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input type="time" className="h-8 w-28" value={w.end} onChange={(e) => updateWindow(i, { end: e.target.value })} />
                  </>
                )}
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox
                    checked={exact}
                    onCheckedChange={(c) =>
                      updateWindow(i, c === true ? { end: w.start } : { end: w.start < "18:00" ? addHour(w.start) : w.start })
                    }
                  />
                  Exact time
                </label>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setWindows((ws) => ws.filter((_, j) => j !== i))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )
        })}
        <Button type="button" size="sm" variant="ghost" className="h-7 gap-1" onClick={() => setWindows((ws) => [...ws, emptyWindow()])}>
          <Plus className="h-3 w-3" /> Add time
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor="pref-nb">Not before</Label>
          <Input id="pref-nb" type="date" value={notBefore} onChange={(e) => setNotBefore(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pref-lang">Language</Label>
          <select
            id="pref-lang"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
          >
            <option value="">Not set</option>
            <option value="en">English</option>
            <option value="es">Spanish</option>
          </select>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="pref-avoid">Dates to avoid</Label>
        <div className="flex gap-2">
          <Input id="pref-avoid" type="date" value={avoidInput} onChange={(e) => setAvoidInput(e.target.value)} />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-10"
            disabled={!avoidInput || avoid.includes(avoidInput)}
            onClick={() => {
              setAvoid((a) => [...a, avoidInput].sort())
              setAvoidInput("")
            }}
          >
            Add
          </Button>
        </div>
        {avoid.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {avoid.map((d) => (
              <span key={d} className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
                {d}
                <button type="button" onClick={() => setAvoid((a) => a.filter((x) => x !== d))} aria-label={`Remove ${d}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Label htmlFor="pref-alt">Alternate phone</Label>
        <Input id="pref-alt" value={altPhone} onChange={(e) => setAltPhone(e.target.value)} placeholder="+1…" />
      </div>

      <div className="space-y-1">
        <Label htmlFor="pref-raw">
          Patient's own words{outsideDaytime ? " (required for this time)" : ""}
        </Label>
        <Textarea
          id="pref-raw"
          rows={2}
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder='e.g. "Call me after my dialysis, around 6 in the evening"'
        />
        <p className={needsWords ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
          Write what the patient said, as they said it. A time before 8:00 AM, after 7:00 PM or on a weekend
          is only allowed when it is the patient's own request, so it needs their words here.
        </p>
      </div>

      {hasPendingCall && (
        <label className="flex items-start gap-2 text-xs">
          <Checkbox checked={movePending} onCheckedChange={(c) => setMovePending(c === true)} className="mt-0.5" />
          <span>
            Move the booked call to the first time that fits this preference. One reminder text for the new
            time goes out on a weekday between 8:00 AM and 7:00 PM Central.{" "}
            {rawText.trim()
              ? "Because this is the patient's own time, it names the day and the exact time."
              : "It names the day only, not the time."}
          </span>
        </label>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      {confirmNeeded ? (
        <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
          <p className="text-sm font-medium">{OUT_OF_HOURS_PROMPT}</p>
          {confirmNeeded.slot && (
            <p className="text-xs text-muted-foreground">The call would be at {formatInTz(confirmNeeded.slot)}.</p>
          )}
          <Label htmlFor="pref-confirm">What the patient said</Label>
          <Textarea
            id="pref-confirm"
            rows={2}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder='e.g. "Yes, 7:30 in the evening is best for me"'
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setConfirmNeeded(null)} disabled={saving}>
              Back
            </Button>
            <Button size="sm" onClick={() => save(confirmText.trim())} disabled={saving || !confirmText.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Patient confirmed, save"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => save()} disabled={saving || needsWords}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save preference"}
          </Button>
        </div>
      )}
    </div>
  )
}

function addHour(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number)
  return `${String(Math.min(h + 1, 23)).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}
